import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";

function DetailRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="tp-detail-row">
      <span className="tp-detail-label">{label}</span>
      <span className="tp-detail-value">{value}</span>
    </div>
  );
}

function TeacherStudentDetail({ loading: parentLoading }) {
  const navigate = useNavigate();
  const { assignmentId, studentId } = useParams();
  const location = useLocation();
  const course = location.state?.course || null;
  const studentPreview = location.state?.student || null;

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        if (course?.course_id != null) params.set("course_id", course.course_id);
        const res = await fetch(
          `${API_BASE_URL}/api/teachers/me/students/${studentId}/overview?${params}`,
          { headers: getAuthHeaders() },
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to load student");
        }
        const data = await res.json();
        if (alive) setOverview(data);
      } catch (err) {
        if (alive) setError(err.message || "Failed to load student");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [studentId, course?.course_id]);

  const back = () => {
    if (assignmentId && assignmentId !== "0") {
      navigate(`/teacher/courses/${assignmentId}`, { state: { course } });
    } else {
      navigate("/teacher/students");
    }
  };

  if (parentLoading || loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading student…</p>
      </div>
    );
  }

  if (error || !overview?.student) {
    return (
      <div className="tp-empty">
        <h3>{error || "Student not found"}</h3>
        <button type="button" className="tp-btn tp-btn--ghost" onClick={back}>
          ← Back
        </button>
      </div>
    );
  }

  const s = overview.student;
  const name = s.name || studentPreview?.name || "Student";

  return (
    <div className="tp-student-detail-page">
      <div className="tp-page-header tp-page-header--back-left">
        <button type="button" className="tp-back-btn" onClick={back}>
          <span aria-hidden="true">←</span>
          <span>Back</span>
        </button>
        <div>
          <h1>{name}</h1>
          <p>
            <span className="tp-mono">{s.student_id}</span>
            {s.class_name ? (
              <>
                {" · "}
                <span className="tp-chip tp-chip--class">{s.class_name}</span>
              </>
            ) : null}
            {s.class_section ? (
              <span className="tp-chip tp-chip--section" style={{ marginLeft: 6 }}>
                {s.class_section}
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="tp-detail-grid">
        <div className="tp-card">
          <h3 className="tp-section-title">Profile</h3>
          <DetailRow label="Session" value={s.academic_session} />
          <DetailRow label="Grade" value={s.grade} />
          <DetailRow label="Status" value={s.student_status} />
          <DetailRow label="Gender" value={s.gender} />
          <DetailRow label="Date of birth" value={s.date_of_birth} />
          <DetailRow label="Admission date" value={s.admission_date} />
          <DetailRow label="Campus" value={s.campus_name} />
        </div>

        <div className="tp-card">
          <h3 className="tp-section-title">Family &amp; Contact</h3>
          <DetailRow label="Father's name" value={s.father_name} />
          <DetailRow label="Father phone" value={s.father_phone} />
          <DetailRow label="Father CNIC" value={s.father_cnic} />
          <DetailRow label="Guardian" value={s.guardian_name} />
          <DetailRow label="Guardian relation" value={s.guardian_relation} />
          <DetailRow label="Guardian phone" value={s.guardian_phone} />
          <DetailRow label="Emergency contact" value={s.emergency_contact_name} />
          <DetailRow label="Emergency number" value={s.emergency_contact_number} />
          <DetailRow label="Current address" value={s.current_address} />
        </div>
      </div>

      <div className="tp-card tp-card--section">
        <h3 className="tp-section-title">Remarks</h3>
        {overview.remarks?.length ? (
          <ul className="tp-mini-list">
            {overview.remarks.map((r) => (
              <li key={r.id}>
                <strong>{r.remark_date}</strong>
                <span>{r.remark}</span>
                <small>{[r.course_name, r.class_name].filter(Boolean).join(" · ")}</small>
              </li>
            ))}
          </ul>
        ) : (
          <p className="tp-muted">No remarks recorded yet.</p>
        )}
      </div>

      <div className="tp-card tp-card--section">
        <h3 className="tp-section-title">Assigned Homework</h3>
        {overview.homework?.length ? (
          <ul className="tp-mini-list">
            {overview.homework.map((hw) => (
              <li key={hw.id}>
                <button
                  type="button"
                  className="tp-link-btn"
                  onClick={() =>
                    navigate(
                      `/teacher/courses/${assignmentId || "0"}/homework/${hw.id}`,
                      { state: { course, homework: hw } },
                    )
                  }
                >
                  <strong>{hw.title}</strong>
                </button>
                <span>Due: {hw.due_date || "—"}</span>
                <small>{hw.course_name} · {hw.class_name}</small>
              </li>
            ))}
          </ul>
        ) : (
          <p className="tp-muted">No homework assigned to this student.</p>
        )}
      </div>

      <div className="tp-card tp-card--section">
        <h3 className="tp-section-title">Course Materials</h3>
        {overview.materials?.length ? (
          <ul className="tp-mini-list">
            {overview.materials.map((m) => (
              <li key={m.id}>
                <strong>{m.title}</strong>
                <span className="tp-chip tp-chip--class">{m.material_type}</span>
                <a href={`${API_BASE_URL}${m.file_path}`} target="_blank" rel="noreferrer">
                  {m.file_name}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="tp-muted">No materials uploaded for this class yet.</p>
        )}
      </div>
    </div>
  );
}

export default TeacherStudentDetail;
